<template>
  <div class="modal-backdrop" @click.self="$emit('close')">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ task ? t('taskForm.editTask') : t('taskForm.newTask') }}</h2>
        <button class="btn btn-icon modal-close" @click="$emit('close')">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <form @submit.prevent="submit">
        <div class="form-group">
          <label>{{ t('taskForm.title') }} *</label>
          <input v-model="form.title" class="form-control" :placeholder="t('taskForm.titlePlaceholder')" required maxlength="200" />
        </div>

        <div class="form-group">
          <label>{{ t('taskForm.description') }}</label>
          <textarea v-model="form.description" class="form-control" rows="3" :placeholder="t('taskForm.descriptionPlaceholder')"></textarea>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>{{ t('taskForm.urgency') }}</label>
            <select v-model="form.urgency" class="form-control">
              <option value="low">{{ t('urgency.low') }}</option>
              <option value="normal">{{ t('urgency.normal') }}</option>
              <option value="high">{{ t('urgency.high') }}</option>
              <option value="critical">{{ t('urgency.critical') }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>{{ t('taskForm.status') }}</label>
            <select v-model="form.status" class="form-control">
              <option value="open">{{ t('status.open') }}</option>
              <option value="in_progress">{{ t('status.in_progress') }}</option>
              <option value="done">{{ t('status.done') }}</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>{{ t('taskForm.project') }}</label>
            <select v-model="form.project_id" class="form-control">
              <option :value="null">{{ t('common.none') }}</option>
              <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>{{ t('taskForm.dueDate') }}</label>
            <input v-model="form.due_at" type="date" class="form-control" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>{{ t('taskForm.visibility') }}</label>
            <div class="radio-group">
              <label class="radio-opt">
                <input type="radio" v-model="form.visibility" value="shared" :disabled="lockedPrivate" />
                <span>{{ t('taskForm.shared') }}</span>
              </label>
              <label class="radio-opt">
                <input type="radio" v-model="form.visibility" value="private" :disabled="lockedPrivate" />
                <span>{{ t('taskForm.private') }}</span>
              </label>
            </div>
            <p v-if="lockedPrivate" class="vis-locked">{{ t('taskForm.privateProjectLock') }}</p>
          </div>
          <div class="form-group" v-if="isInternal()">
            <label>{{ t('taskForm.assignTo') }}</label>
            <select v-model="form.assigned_to" class="form-control">
              <option :value="null">{{ t('taskForm.unassigned') }}</option>
              <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }}</option>
            </select>
          </div>
        </div>

        <p v-if="error" class="form-error">{{ error }}</p>

        <div class="modal-actions">
          <button type="button" class="btn btn-ghost" @click="$emit('close')">{{ t('common.cancel') }}</button>
          <button type="submit" class="btn btn-primary" :disabled="saving">
            {{ saving ? t('common.saving') : (task ? t('common.saveChanges') : t('taskForm.createTask')) }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import api from '@/api'
import { useAuth } from '@/composables/useAuth'
import { useI18n } from '@/composables/useI18n'
import { useToast } from '@/composables/useToast'

function toLocalDate(str) {
  if (!str) return ''
  const d = new Date(str)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`
}

export default {
  name: 'TaskFormModal',
  props: {
    task:     { type: Object, default: null },
    projects: { type: Array,  default: () => [] },
    // Prefill values for a NEW task (ignored when editing). Lets callers scope
    // creation to a project without faking a `task` object (which would flip the
    // modal into edit mode and break submit).
    defaults: { type: Object, default: () => ({}) },
  },
  emits: ['close', 'saved'],
  setup(props, { emit }) {
    const { isManager, isInternal } = useAuth()
    const { t }         = useI18n()
    const toast         = useToast()
    const saving  = ref(false)
    const error   = ref('')
    const users   = ref([])

    const form = reactive({
      title:       props.task?.title       || '',
      description: props.task?.description || '',
      urgency:     props.task?.urgency     || 'normal',
      status:      props.task?.status      || 'open',
      visibility:  props.task?.visibility  || 'shared',
      project_id:  props.task?.project_id  ?? props.defaults.project_id ?? null,
      assigned_to: props.task?.assigned_to ?? null,
      due_at:      toLocalDate(props.task?.due_at),
    })

    // A task inside a private project is always private (mirrors the backend rule).
    const lockedPrivate = computed(() => {
      const p = props.projects.find(p => p.id === form.project_id)
      return p?.visibility === 'private'
    })
    watch(lockedPrivate, (locked) => { if (locked) form.visibility = 'private' }, { immediate: true })

    onMounted(async () => {
      if (isInternal()) {
        try { users.value = (await api.getUsers()).data || [] } catch {}
      }
    })

    async function submit() {
      error.value  = ''
      saving.value = true
      try {
        const payload = {
          title:       form.title,
          description: form.description || null,
          urgency:     form.urgency,
          status:      form.status,
          visibility:  form.visibility,
          project_id:  form.project_id  || null,
          assigned_to: form.assigned_to || null,
          due_at:      form.due_at      || null,
        }
        let res
        if (props.task) {
          res = await api.updateTask(props.task.id, payload)
        } else {
          res = await api.createTask(payload)
        }
        emit('saved', res.data)
        emit('close')
        toast.success(props.task ? t('toast.saved') : t('toast.taskCreated'))
      } catch {
        error.value = t('toast.saveFailed')
        toast.error(t('toast.saveFailed'))
      } finally {
        saving.value = false
      }
    }

    return { form, users, saving, error, isManager, isInternal, submit, t, lockedPrivate }
  },
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}
.modal {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px;
  box-shadow: var(--shadow);
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}
.modal-header h2 { font-size: 18px; font-weight: 700; }
.modal-close { color: var(--muted); }
.modal-close:hover { color: var(--text); }

.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

.radio-group { display: flex; flex-direction: column; gap: 8px; }
.radio-opt {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  color: var(--muted);
}
.radio-opt:has(input:checked) { color: var(--text); }
.radio-opt input { accent-color: var(--primary); }
.radio-opt input:disabled { cursor: not-allowed; }
.vis-locked { font-size: 11px; color: var(--muted); margin-top: 6px; }

.form-error {
  color: var(--danger);
  font-size: 13px;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: rgba(239,68,68,.1);
  border-radius: 6px;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}
</style>
